/**
 * Authoritative English dictionary. Its shape defines `Dict` (see ../types) — every other
 * locale must provide the same keys. Placeholders use `{name}` and are filled by `t(key, vars)`.
 * Kept as a plain nested object so it stays JSON-serialisable (endpoint-loadable later).
 */
export const en = {
  languageName: 'English',

  common: {
    /** Unit shown after a reward amount when the shop pays in points (currency === "POINT"). */
    points: 'points',
  },

  /**
   * The "subject" word lexicon — the category a brand trades in. Defaults to clothing; a jewelry/shoe
   * brand overrides these per language via `tradeInConfig.terms` (e.g. `{ noun: 'jewelry', … }`).
   * Referenced in copy as {noun} (bare), {nounPoss} ("your X"), {nounDef} ("the X"); use {noun|cap}
   * to capitalise at a sentence start. A brand supplies the FULL phrase per form so Danish/German
   * article agreement (dit/din/dine, det/den/die) comes from the brand's wording, not a grammar engine.
   */
  subject: {
    noun: 'clothing',
    nounPoss: 'your clothing',
    nounDef: 'the clothing',
    // What the customer gets back. Named per language rather than by us: brands run gift cards,
    // vouchers or their own reward systems, so ALL copy interpolates these tokens and none of it says
    // the word — swapping "voucher" for "gift card" is a config change, not a rewrite. Four forms,
    // mirroring the noun ones, because the copy needs all four and a brand's word inflects its own
    // way: bare, possessive, definite, plural. Like the noun forms, each carries the FULL phrase so
    // Danish/German article agreement is the brand's wording ("dit værdibevis", not "dit {reward}").
    reward: 'voucher',
    rewardPoss: 'your voucher',
    rewardDef: 'the voucher',
    rewardPlural: 'vouchers',
  },

  auth: {
    signingIn: 'Signing you in…',
    stillWorking: 'Still working on it — we’re gathering your past orders. This can take a moment.',
    loadingItems: 'Loading your items…',
    errorTitle: "We couldn't start your trade-in",
    errorGeneric: 'Something went wrong signing you in.',
    missingShop: 'App is misconfigured — missing shopId.',
    needSignIn: 'You need to sign in before trading in.',
    signInFailed: 'Sign-in failed. Please try again.',
    linkInvalid: 'This login link no longer works. The profile it belongs to may have been deleted. Go back to the front page to log in again.',
    backToFrontPage: 'Back to the front page',
  },

  config: {
    title: 'Configuration error',
    missingShop: 'This trade-in is missing its shop configuration (shopId). Please contact the store.',
  },

  login: {
    title: 'Give {nounPoss} a second life',
    subtitle: 'Log in to start your trade-in and turn pieces you no longer wear into a {reward}.',
    emailLabel: 'Email',
    emailPlaceholder: 'you@example.com',
    start: 'Continue',
    working: 'Please wait…',
    emailSentTitle: 'Check your email',
    emailSentBody: 'We’ve sent a login link to {email}. Open it on this device to continue.',
    phoneLabel: 'Phone number',
    phonePlaceholder: '12 34 56 78',
    sendCode: 'Send code',
    codeLabel: 'Verification code',
    codePlaceholder: '6-digit code',
    verify: 'Verify & continue',
    changePhone: 'Use a different number',
    back: 'Back',
    methodSms: 'Log in with SMS',
    methodSmsDesc: 'We’ll send you a code via SMS — if we have your number on file.',
    methodEmail: 'Log in with email',
    methodEmailDesc: 'We’ll send a login link to your inbox.',
    or: 'or',
    // Not a retailer login: for customers whose item didn't come from the brand's own webshop —
    // bought at a retailer or received as a gift. Creates a fresh consumer so they can trade it in.
    wholesale: 'Bought in-store or gifted?',
    emailInvalid: 'Enter a valid email address.',
    phoneInvalid: 'Enter a valid phone number.',
    codeInvalid: 'That code didn’t work. Try again.',
    failed: 'Something went wrong. Please try again.',
    step1Title: 'Find your pieces',
    step1Body: 'Search or snap a photo of what you no longer wear.',
    step2Title: 'Send them in',
    step2Body: 'Pack everything in one parcel with your prepaid label.',
    step3Title: 'Get {rewardPoss}',
    step3Body: {
      received: 'Receive {rewardPoss} once we’ve received your items.',
      handin: 'Receive {rewardPoss} as soon as your parcel is scanned at the drop-off point.',
      instant: 'Get {rewardPoss} right away — then send your items to us.',
    },
  },

  country: {
    title: 'Your country may not be supported',
    body: "It looks like we may not have a carrier that can ship a trade-in from your country to our warehouse. You're welcome to continue, but we may not be able to complete the collection.",
    dismiss: 'Continue anyway',
  },

  menu: {
    open: 'Menu',
    language: 'Language',
    myTradeIns: 'My trade-ins',
    howItWorks: 'How it works',
    noneYet: 'None yet',
    logout: 'Log out',
    soon: 'Soon',
  },

  // Voucher rows, reused by the "My trade-ins" page for the vouchers section.
  rewards: {
    activeHeading: 'Available',
    spentHeading: 'Spent',
    expires: 'Expires {date}',
    active: 'Active',
    spentBadge: 'Spent',
  },
  // "My trade-ins" account page: vouchers first, then the trade-in history + detail dialog.
  myTradeIns: {
    title: 'My trade-ins',
    subtitle: 'Your trade-ins and {rewardPlural}.',
    back: 'Back to trade-in',
    loading: 'Loading your trade-ins…',
    empty: 'You don’t have any trade-ins or {rewardPlural} yet.',
    historyHeading: 'Your trade-ins',
    tradeInPrefix: 'Trade-in',
    itemsCount: '{count} items',
    statusReceived: 'Received',
    statusOnItsWay: 'On its way to us',
    statusAwaiting: 'Awaiting your parcel',
    detailTitle: 'Trade-in details',
    nextHeading: 'What happens now',
    nextReceived: 'We’ve received your items — {rewardPoss} is on its way.',
    nextTransit: 'Your parcel is on its way to us. We’ll email you once we’ve received and checked your items.',
    nextPickup: 'Bring your items to our store and we’ll take care of the rest.',
    nextShip: 'Pack your items and send them to us using the details below.',
    itemsHeading: 'Items in this trade-in',
    labelUnavailable: 'The shipping label is no longer available.',
    close: 'Close',
  },

  steps: {
    items: 'Items',
    story: 'Your story',
    details: 'Details',
    shipping: 'Shipping',
    confirm: 'Confirm',
  },

  rail: {
    title: 'Your trade-in',
    itemCount: '{count} item',
    itemCountPlural: '{count} items',
    empty: 'No items yet — add your first piece and watch the value add up.',
    loading: 'Loading your trade-in…',
    estimatedReward: 'Total value',
    rewardNoteConfirmed: {
      received: 'Confirmed when we receive your items',
      handin: 'Confirmed when you drop off your parcel',
      instant: 'Issued as soon as you confirm your trade-in',
    },
    rewardNoteAdd: 'Add items to see the value',
    addedManually: 'Added manually',
    maxExceeded: 'This trade-in exceeds the maximum of {amount}. Remove an item to continue.',
    remove: 'Remove {name}',
    back: 'Back',
    continue: 'Continue',
    confirmTradeIn: 'Confirm trade-in',
    done: 'Done',
  },

  items: {
    firstTitle: "Let's find your first item",
    firstSubtitle: 'Take a photo, or search the catalogue.',
    anotherTitle: 'Add another item?',
    anotherSubtitle: 'Review everything before you continue.',
    selectTitle: 'Select items to trade in',
    selectSubtitle: 'From your past orders — tap to add.',
    manualTitle: 'Add an item manually',
    manualSubtitle: 'For items bought elsewhere.',
    estReward: 'Value',
    bought: 'Bought {date}',
    addManually: 'Add manually',
    boughtElsewhere: 'Bought elsewhere?',
    addedCount: '{count} added',
    nothingTitle: 'Nothing to trade in',
    nothingBody:
      'We couldn’t find any items on your account that are eligible for trade-in. Trade-in here is only available for items purchased from us — once you have an eligible order, it’ll show up here.',
  },

  /**
   * First-run explainer (C2S-883), shown once per shop over the empty items step. Same three beats as
   * the login page's how-it-works section and the Confirm page's pack → drop off → reward structure,
   * so the customer meets one story throughout. Step 3 is strategy-variant.
   */
  intro: {
    title: 'How trade-in works',
    lead: 'Trade in {brand} {noun} you no longer wear — and get {rewardPoss} to spend on new or secondhand pieces.',
    step1Title: 'Find your pieces',
    step1Body: 'Take a photo of what you no longer wear, or search our catalogue. Add as many pieces as you like.',
    step2Title: 'Send them in',
    step2Body: 'Pack everything in one parcel with your prepaid label — the shipping is on us.',
    step3Title: 'Get {rewardPoss}',
    step3Body: {
      received: 'Once we’ve received and checked your items, we’ll email {rewardPoss}.',
      handin: 'The moment your parcel is scanned at drop-off, we’ll email {rewardPoss}.',
      instant: '{rewardPoss|cap} is emailed as soon as you confirm — then you send us your items.',
    },
    cta: 'Got it',
    close: 'Close',
  },

  finder: {
    search: 'Search catalogue',
    photo: 'Add by photo',
    searchTitle: 'Search the catalogue',
    searchSub: 'By SKU, EAN or product name.',
    searchPlaceholder: 'Search by SKU, EAN or product name',
    carelabelHint: 'Tip: find the SKU or style number on your item’s care label.',
    searching: 'Searching…',
    searchFailed: 'Search failed — try again.',
    noMatches: 'No matches for “{query}”.',
    unavailable: 'Catalogue search is unavailable for this shop.',
    estReward: 'Value',
    notEligible: 'Not eligible',
    add: 'Add',
    adding: 'Adding…',
    added: '{name} added · +{amount}',
    backToOrders: 'Back to your orders',
    showMore: 'Show more results',
    loadingMore: 'Loading…',
  },

  photo: {
    dropTitle: 'Take a photo of your item',
    dropHint: 'We’ll find similar items in our catalogue.',
    or: 'or',
    wayDevice: 'Take or upload a photo',
    wayPhone: 'Use your phone’s camera',
    qrAgainTitle: 'Try another photo',
    qrAgainHint: 'Take another photo on your phone and we’ll search again automatically.',
    analysingTitle: 'Finding your best matches…',
    analysingHint: 'Comparing your photo to our catalogue',
    uploadingTitle: 'Uploading your photo…',
    uploadingHint: 'Just a moment',
    yourPhoto: 'Your photo',
    matchedTo: 'Matched to our catalogue',
    replace: 'Replace',
    headingConfident: 'Does one of these look like your item?',
    headingCategory: 'Closest matches in {category}',
    noMatchHeading: 'We couldn’t find a close match',
    noMatchBody:
      'None of our catalogue items matched closely. Try another photo, or search by name instead.',
    // Distinct from "no match": the search never ran, so the item may well be in the catalogue.
    notSearched: 'Not searched yet',
    searchFailedHeading: 'We couldn’t search right now',
    searchFailedBody:
      'Something went wrong on our side, so we haven’t compared your photo yet. Your photo is saved — please try again.',
    tryAgain: 'Try again',
    refine: 'Refine by category',
    clear: 'Clear',
    noneOfThese: 'None of these',
    // "Add by category" fallback, reached from the placeholder tile in the photo results.
    categoryCardTitle: 'Can’t find your item?',
    categoryCardHint: 'Choose a category instead',
    categoryPickTitle: 'What kind of item is it?',
    categoryPickHint: 'We’ll use the photo you just took.',
    categoryValue: 'Value',
    categoryConfirm: 'Add to trade-in',
    categoryBack: 'Back',
    uploadAnother: 'Upload another photo',
    bestMatch: 'Best match',
    uploadFailed: 'Something went wrong — please try again.',
  },

  match: {
    strong: 'Strong match',
    colour: 'Colour: {color}',
    estimatedReward: 'Value',
    backToMatches: 'Back to matches',
    backToResults: 'Back to results',
    confirm: 'Yes, this is my item',
  },

  details: {
    // Not where *we* send it — it's the customer's own address, the sender on the prepaid label
    // we print to route their parcel to our warehouse.
    title: 'Your details',
    subtitle: 'So we can create your prepaid shipping label and send {rewardPoss}.',
    contact: 'Contact',
    address: 'Address',
    fullName: 'Full name',
    email: 'Email',
    country: 'Country',
    streetAddress: 'Street address',
    postalCode: 'Postal code',
    city: 'City',
    prefix: 'Country code',
    phone: 'Phone',
    emailLocked: 'Your email is linked to your account and can’t be changed here.',
    consentPre: 'I accept the ',
    consentLink: 'trade-in terms & conditions',
    consentPost: ' and confirm the items are mine to trade in.',
    required: 'This field is required.',
    emailInvalid: 'Enter a valid email address.',
    phoneInvalid: 'Enter a valid phone number.',
    zipInvalid: 'Enter a valid postal code',
    zipFormat: 'format: {format}',
    loading: 'Loading your details…',
    saveFailed: 'We couldn’t save your details. Please try again.',
  },

  terms: {
    title: 'Terms & conditions for trading in {noun}',
    // {brand} is the shop's brand name (config.brandName), or a generic fallback when absent.
    introLead:
      'As part of our secondhand initiative, you can trade in your used {brand} {noun} for a {reward}.',
    introAccept:
      'By sending in your used {brand} {noun}, you accept that a {reward} is only issued for {noun} that meets the conditions below, and that {noun} which does not meet the requirements will NOT be returned to you.',
    introDonate:
      '{noun|cap} that does not meet our requirements is donated to charity or given to companies that can reuse the items or their materials.',
    requirementsTitle: 'Requirements for the {noun} you hand in',
    requirementsBody:
      'We accept {brand} {noun} that is clean and where all original labels are intact — both the neck label and the care label (washing instructions etc.).',
    howToTitle: 'How to hand in your {noun}',
    howToRegister:
      'You register the {brand} products you wish to hand in. During registration you can see the value of each product — the amount you will receive as a {reward} in exchange for your {noun}.',
    howToAdjust:
      'If what we receive does not match what you registered, we correct it to the right product and adjust your total reward accordingly. By submitting {nounPoss}, you accept that we make this adjustment.',
    howToLabel:
      'When you have completed your registration, you receive a shipping label or QR code to send in your {noun}. This shipping label is prepaid by {brand}.',
    howToFinal:
      'Once you have handed in your {noun}, you cannot undo your submission, and we do not return your products.',
    voucherTitle: '{reward|cap}',
    voucherIssue: {
      received:
        'You receive a {reward} once we have confirmed that the {noun} you sent in meets the requirements above. {rewardDef|cap} is sent to the email address you provided.',
      handin:
        'You receive a {reward} once your parcel has been registered at the drop-off point. {rewardDef|cap} is sent to the email address you provided. If the {noun} you send in does not meet the requirements above, {rewardDef} may be withdrawn.',
      instant:
        'You receive a {reward} as soon as you register your trade-in. {rewardDef|cap} is sent to the email address you provided. If the {noun} you send in is not sent to us, or does not meet the requirements above, {rewardDef} may be withdrawn.',
    },
    voucherValidity:
      '{rewardDef|cap} is valid for 3 years from issue and can be used for purchases at {brand} online. {rewardDef|cap} cannot be exchanged for cash.',
    closing: 'We look forward to receiving your {noun}.',
    brandFallback: 'the brand',
    close: 'Close',
    accept: 'Accept & continue',
  },

  shipping: {
    title: 'How will you send it in?',
    subtitle: 'Pack your {count} item — one label for all.',
    subtitlePlural: 'Pack your {count} items — one label for all.',
    loading: 'Loading shipping options…',
    none: 'No shipping service is available for your address yet.',
    maxExceeded: 'This trade-in exceeds the maximum value. Remove an item and try again.',
    createFailed: 'We couldn’t create your trade-in. Please try again.',
    // Optional descriptions, looked up by the carrier's localizationKey; the title comes from the
    // carrier itself. Unknown carriers simply show no description (graceful fallback).
    desc_gls: 'Drop off at a GLS parcel shop. Free, printed label.',
    desc_dhl: 'Drop off at a DHL parcel shop. Free, printed label.',
    desc_dao: 'A courier collects from your door.',
    desc_shipmondo: 'Drop off with a printed label.',
    desc_pickup: 'Drop off at our physical store.',
    desc_postnord: 'Drop off at any PostNord parcel point.',
  },

  story: {
    title: 'Tell us your story',
    subtitle: 'Optional — we love hearing about your pieces.',
    placeholder: 'Tell us about this piece — where you wore it, why you loved it…',
    consent:
      'You may share my story (anonymously) to inspire other customers to give their {noun} a second life.',
  },

  confirm: {
    topbarTitle: 'Confirm',
    heroTitle: 'You’re all set',
    heroBody: {
      received: 'Your trade-in is registered. Follow the three steps below and {rewardPoss} is on its way.',
      handin:
        'Your trade-in is registered. Follow the three steps below — {rewardPoss} is issued as soon as you drop off your parcel.',
      instant:
        'Your trade-in is registered and {rewardPoss} is on its way. Just follow the three steps below to send us your items.',
    },
    packTitle: 'Pack your {count} item',
    packTitlePlural: 'Pack your {count} items',
    packBody: 'Put everything in one parcel. No special packaging needed — reuse a box or bag.',
    pickupPackBody: 'Pack your items and bring them to our physical store — no shipping label needed.',
    shippingCode: 'Shipping code',
    generatingLabel: 'Generating your shipping label…',
    labelFailed: 'We couldn’t generate your label. Please contact support.',
    showQr: 'Show QR code',
    showLabel: 'Show shipping label',
    dropTitle: 'Drop it off',
    dropBody: 'Show the code or label at your carrier’s drop-off point. ',
    findShop: 'Find nearest drop-off point →',
    pickupTitle: 'Drop off in-store',
    pickupBody: 'Bring your items to our store, and we’ll take care of the rest.',
    rewardTitle: 'Get {rewardPoss}',
    rewardBodyPre: {
      received: 'Once we receive your parcel, we’ll email your ',
      handin: 'As soon as your parcel is scanned at drop-off, we’ll email your ',
      instant: 'Your ',
    },
    rewardBodyPost: {
      received: ' {reward} within 2 business days.',
      handin: ' {reward}. Please allow a couple of hours for the drop-off tracking to reach us.',
      instant: ' {reward} is already on its way to your inbox — just send us your items using the steps above.',
    },
    // INSTANT strategy: the voucher code is shown right here once Core has issued it.
    voucherReadyTitle: '{rewardPoss|cap} is ready',
    voucherIssuing: 'Issuing {rewardPoss}…',
    voucherExpires: 'expires {date}',
    copyCode: 'Copy {reward} code',
    codeCopied: '{reward|cap} code copied',
    startAnother: 'Start another trade-in',
    backToShop: 'Back to shop',
  },

  dropoff: {
    title: 'Nearest drop-off points',
    qrSupported: 'Supports QR drop-off — no printed label needed.',
    qrNotSupported: 'Requires a printed label.',
  },
}
